import { useState } from "react";
import { useParams } from "react-router";
import { useTeamReport, useTeams, useAuditSummary } from "@/api/hooks";
import { useAuth } from "@/providers/auth-provider";
import { Skeleton } from "@/components/ui/skeleton";
import { EmptyState } from "@/components/ui/empty-state";
import { SectionHeader } from "@/components/ui/section-header";
import { StatusBadge } from "@/components/ui/status-badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { PhotoCategoryGrid } from "@/components/photo-category-grid";
import { ActivityLog } from "@/components/activity-log";
import { TeamFlag } from "@/components/team-flag";
import { YawGauge } from "@/components/yaw-gauge";
import { IconAlertTriangle, IconLock } from "@tabler/icons-react";
import { computeSide, type SideMeasurements } from "@/domain/yaw-check";
import { formatDateTime, timeAgo } from "@/lib/time";
import { asEventId, asBuildId, type PhotoCategoryId } from "@/types/identifiers";
import { getWeighingSections } from "@/lib/photo-categories";
import type { PhotoCategory } from "@/api/hooks/use-photo-categories";
import type { BuildPhoto } from "@/api/hooks/use-build-photos";
import type { components } from "@/api/generated/schema";

type YawCheck = components["schemas"]["YawCheckResponse"];
type ToeCheck = components["schemas"]["ToeCheckResponse"];

function extractSide(check: YawCheck, side: "prt" | "stb"): SideMeasurements {
  return {
    fwd_static: check[`${side}_fwd_static`],
    fwd_max: check[`${side}_fwd_max`],
    fwd_min: check[`${side}_fwd_min`],
    aft_static: check[`${side}_aft_static`],
    aft_max: check[`${side}_aft_max`],
    aft_min: check[`${side}_aft_min`],
  };
}

function sideIncomplete(m: SideMeasurements): boolean {
  const c = computeSide(m);
  return c.tot_static === 0 || c.tot_max === 0 || c.tot_min === 0;
}

function ReadOnlyYawCard({ check }: { check: YawCheck }) {
  const prt = computeSide(extractSide(check, "prt"));
  const stb = computeSide(extractSide(check, "stb"));
  const prtInc = sideIncomplete(extractSide(check, "prt"));
  const stbInc = sideIncomplete(extractSide(check, "stb"));
  const hasMissing = prtInc || stbInc;
  const isSigned = !!check.signed_off_at;
  const borderColor = hasMissing ? "border-l-amber-400" : isSigned ? "border-l-green-500" : "border-l-blue-400";

  return (
    <div className={`border-l-3 rounded-r-lg bg-card pl-5 pr-5 py-3 ${borderColor}`}>
      <div className="flex items-center gap-2 text-xs mb-2">
        <span className="font-medium">YAW</span>
        {hasMissing && (
          <span className="flex items-center gap-1 text-amber-500">
            <IconAlertTriangle className="h-3.5 w-3.5" />
            <span className="text-[11px]">{prtInc && stbInc ? "PORT + STBD incomplete" : prtInc ? "PORT incomplete" : "STBD incomplete"}</span>
          </span>
        )}
      </div>
      <div className="space-y-1.5">
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground w-7 shrink-0">PRT</span>
          <YawGauge staticYaw={prt.tot_yaw} minYaw={prt.tot_min_yaw} maxYaw={prt.tot_max_yaw} />
          <span className="text-xs tabular-nums shrink-0 w-12 text-right">{prt.tot_yaw.toFixed(2)}°</span>
          <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">±{prt.range.toFixed(2)}</span>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] text-muted-foreground w-7 shrink-0">STB</span>
          <YawGauge staticYaw={stb.tot_yaw} minYaw={stb.tot_min_yaw} maxYaw={stb.tot_max_yaw} />
          <span className="text-xs tabular-nums shrink-0 w-12 text-right">{stb.tot_yaw.toFixed(2)}°</span>
          <span className="text-[10px] text-muted-foreground tabular-nums shrink-0">±{stb.range.toFixed(2)}</span>
        </div>
      </div>
      <div className="mt-2 text-xs text-muted-foreground">
        Checked by <span className="font-medium text-foreground">{check.checked_by}</span>
        <span className="text-muted-foreground/50 ml-1.5">{timeAgo(check.checked_at)}</span>
      </div>
      {isSigned && (
        <div className="mt-1 text-[11px] font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
          <IconLock className="h-3 w-3" />
          Signed off by {check.signed_off_by}
        </div>
      )}
    </div>
  );
}

function ReadOnlyToeCard({ check }: { check: ToeCheck }) {
  const isSigned = !!check.signed_off_at;
  const borderColor = isSigned ? "border-l-green-500" : "border-l-blue-400";

  return (
    <div className={`border-l-3 rounded-r-lg bg-card pl-5 pr-5 py-3 flex items-center gap-3 ${borderColor}`}>
      <div className="flex-1 min-w-0">
        <div className="text-xs">
          <span className="font-medium">Toe Check</span>
        </div>
        <div className="mt-1 text-xs text-muted-foreground">
          Checked by <span className="font-medium text-foreground">{check.checked_by}</span>
          <span className="text-muted-foreground/50 ml-1.5">{timeAgo(check.checked_at)}</span>
        </div>
        {isSigned && (
          <div className="mt-1 text-[11px] font-medium text-green-600 dark:text-green-400 flex items-center gap-1">
            <IconLock className="h-3 w-3" />
            Signed off by {check.signed_off_by}
          </div>
        )}
      </div>
      {check.photo?.thumbnail_url && (
        <img
          src={check.photo.thumbnail_url}
          alt="Toe check"
          className="w-12 h-16 rounded-md object-cover ring-1 ring-border shrink-0"
        />
      )}
    </div>
  );
}

const STATE_VARIANTS: Record<string, "success" | "warning" | "info" | "neutral"> = {
  golden: "warning",
  initial: "info",
  draft: "neutral",
};

export function TeamReportPage() {
  const { eventId: rawEventId } = useParams();
  const eventId = rawEventId ? asEventId(rawEventId) : undefined;
  const { can } = useAuth();
  const canViewAllTeams = can("view-fleet");

  const [selectedTeam, setSelectedTeam] = useState<string | undefined>(undefined);
  // Only admins/editors render the team selector; viewers must not fetch
  // /api/teams (view-builds) or the 403 nukes the whole page.
  const { data: teams } = useTeams(canViewAllTeams);
  const { data: report, isLoading } = useTeamReport(eventId!, selectedTeam);
  const buildId = report?.build_id ? asBuildId(report.build_id) : undefined;
  const { data: activityItems } = useAuditSummary(buildId!);

  if (!eventId) {
    return <EmptyState message="No event selected." />;
  }

  if (isLoading) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-12 w-64 rounded-lg" />
        <Skeleton className="h-32 w-full rounded-lg" />
        <Skeleton className="h-32 w-full rounded-lg" />
      </div>
    );
  }

  if (!report) {
    return <EmptyState message="No build found for your team at this event." />;
  }

  const photos = report.weighing_photos as BuildPhoto[];
  const photosByCategoryId = new Map<PhotoCategoryId, BuildPhoto[]>();
  for (const p of photos) {
    const arr = photosByCategoryId.get(p.category_id);
    if (arr) arr.push(p);
    else photosByCategoryId.set(p.category_id, [p]);
  }
  const sections = getWeighingSections(report.photo_categories as PhotoCategory[]);

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          {report.team_iso_alpha2 && <TeamFlag iso_alpha2={report.team_iso_alpha2} name={report.team_name} size="md" />}
          <div>
            <h1 className="text-lg font-semibold">{report.team_name}</h1>
            <p className="text-sm text-muted-foreground">
              {report.event_name} &middot; Boat {report.boat_number}
            </p>
          </div>
        </div>

        {canViewAllTeams && teams && teams.length > 0 && (
          <Select
            value={selectedTeam ?? report.team_iso_alpha2}
            onValueChange={setSelectedTeam}
          >
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Select team" />
            </SelectTrigger>
            <SelectContent>
              {teams.map((team) => (
                <SelectItem key={team.id} value={team.iso_alpha2}>
                  {team.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}
      </div>

      {/* Calibrations */}
      <section>
        <SectionHeader>Calibrations</SectionHeader>
        {report.calibrations.length === 0 ? (
          <p className="text-sm text-muted-foreground">No calibrations yet.</p>
        ) : (
          <div className="grid gap-2 sm:grid-cols-2">
            {report.calibrations.map((cal) => {
              const isGolden = report.golden_calibration?.id === cal.id;
              const borderColor = isGolden
                ? "border-l-amber-500"
                : cal.state === "initial"
                  ? "border-l-blue-400"
                  : "border-l-border";
              return (
                <div
                  key={cal.id}
                  className={`bg-card border-l-3 ${borderColor} rounded-r-lg pl-4 pr-5 py-3`}
                >
                  <div className="flex items-center justify-between mb-2">
                    <StatusBadge variant={STATE_VARIANTS[cal.state] ?? "neutral"}>
                      {cal.state}
                    </StatusBadge>
                    <span className="text-xs text-muted-foreground">
                      {cal.parameter_count} params
                    </span>
                  </div>
                  <div className="text-xs text-muted-foreground space-y-0.5">
                    <div>Fetched: {formatDateTime(cal.fetched_at)}</div>
                    {cal.signed_off_at && (
                      <div>
                        Signed off: {formatDateTime(cal.signed_off_at)}
                        {cal.signed_off_by && ` by ${cal.signed_off_by}`}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </section>

      {/* Checks */}
      {(report.toe_checks.length > 0 || report.yaw_checks.length > 0) && (
        <section>
          <SectionHeader>Checks</SectionHeader>
          <div className="space-y-2">
            {report.toe_checks.map((tc) => (
              <ReadOnlyToeCard key={tc.id} check={tc} />
            ))}
            {report.yaw_checks.map((yc) => (
              <ReadOnlyYawCard key={yc.id} check={yc} />
            ))}
          </div>
        </section>
      )}

      {/* Weighing Photos */}
      <section>
        <SectionHeader>Weighing Photos</SectionHeader>
        <PhotoCategoryGrid
          sections={sections}
          photosByCategoryId={photosByCategoryId}
        />
      </section>

      {/* Activity */}
      {activityItems && activityItems.length > 0 && (
        <section>
          <SectionHeader>Activity</SectionHeader>
          <ActivityLog items={activityItems} />
        </section>
      )}
    </div>
  );
}
